import React from 'react';
import StyledAppWrapper from '@common/components/AppWrapper';
import Footer from '@common/components/Footer';
import { Menu } from '@common/components/Menu';
import { Announcement } from '@common/components/Announcement';
import { Logo } from './components/Logo';
import { LanguageSwitcher } from '@common/components/LanguageSwitcher';
import { ActiveMenuProvider } from '@common/contexts/ActiveMenuContext';
import { Property } from '@root/../shared-types/Property';
import { Guest } from '@root/../shared-types/Common';
import { Header, HeaderPosition, MobileOnly, DesktopOnly } from './GeneralLayout.styles';
import { LeftAlignedHeader } from './components/HeaderLayout/LeftAlignedHeader/LeftAlignedHeader';
import { CenterAlignedHeader } from './components/HeaderLayout/CenterAlignedHeader/CenterAlignedHeader';
import { SignIn } from './components/HeaderItems/SignIn/SignIn';
import { Basket } from './components/HeaderItems/Basket/Basket';
import { useModuleStatuses } from '@features/properties';

export type LayoutProps = {
  children: React.ReactNode;
  property: Property;
  language: string;
  basketCount: number;
  userHasAccessToSpaClasses: boolean;
  userHasAccessToFacilities: boolean;
  termsAndConditionPage: PageMeta | null;
  privacyAndCookiePolicyPage: PageMeta | null;
  cancellationPage: PageMeta | null;
  guest: Guest | null;
};

type PageMeta = {
  id: number;
  property: {
    id: number;
  };
  title: string;
  slug: string;
  heroBackgroundImage: string | null;
};

export function GeneralLayout({
  children,
  property,
  language = '',
  basketCount = 0,
  termsAndConditionPage = null,
  privacyAndCookiePolicyPage = null,
  cancellationPage = null,
  guest = null,
  userHasAccessToSpaClasses,
  userHasAccessToFacilities,
}: LayoutProps) {
  const headerPosition: HeaderPosition =
    (property?.layoutData?.headerPosition as HeaderPosition) ?? 'left';

  const isLoggedIn = !!guest;

  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();

  // Build up header items that will be used in either the leftAlignedHeader or the centerAlignedHeader
  const logo = <Logo logo={property.logo} name={property.name} />;
  const languageSwitcher = (
    <LanguageSwitcher
      availableLanguages={[...(property.additionalLanguages || []), property.defaultLanguage]}
      currentLanguage={language}
    />
  );
  const signIn = <SignIn guest={guest} />;
  const basket = <Basket basketCount={basketCount} />;
  const menu = (
    <Menu
      isLoggedIn={isLoggedIn}
      isRoomsIntegrated={isRoomsEnabled}
      isTablesIntegrated={property.isTablesIntegrated}
      isRetailEnabled={property.isRetailEnabled}
      isSpaTreatmentEnabled={property.modulesExcludingDisabled?.spa?.features?.treatments?.enabled}
      isFacilitiesEnabled={userHasAccessToFacilities}
      isSpaClassesEnabled={userHasAccessToSpaClasses}
      isSpaDaysEnabled={property.modulesExcludingDisabled?.spa?.features?.days?.enabled}
      websiteUrl={property.websiteUrl}
    />
  );

  // in centerX views, assign nav items to either left/right items.
  //
  // Desktop and mobile have different layouts, so use MobileOnly/DesktopOnly wrappers to show/hide
  // them accordingly.
  let leftItems: React.ReactNode[] = [];
  let rightItems: React.ReactNode[] = [];

  if (headerPosition === 'centerLeft') {
    leftItems = [menu, <DesktopOnly key="lang-desktop">{languageSwitcher}</DesktopOnly>];
    rightItems = [<MobileOnly key="lang-mobile">{languageSwitcher}</MobileOnly>, basket, signIn];
  } else if (headerPosition === 'centerRight') {
    leftItems = [signIn, languageSwitcher];
    rightItems = [basket, menu];
  }

  return (
    <ActiveMenuProvider>
      <StyledAppWrapper hasNavbar={false}>
        <Header headerPosition={headerPosition}>
          {headerPosition === 'centerLeft' || headerPosition === 'centerRight' ? (
            <CenterAlignedHeader logo={logo} leftItems={leftItems} rightItems={rightItems} />
          ) : (
            <LeftAlignedHeader logo={logo} rightItems={[languageSwitcher, signIn, basket, menu]} />
          )}
        </Header>

        <Announcement slug={property.slug} />

        {children}

        <Footer
          property={property}
          termsAndConditionPage={termsAndConditionPage}
          privacyAndCookiePolicyPage={privacyAndCookiePolicyPage}
          cancellationPage={cancellationPage}
        />
      </StyledAppWrapper>
    </ActiveMenuProvider>
  );
}
